/**
 * Banka bakiyesi kontrolü — gün sonunda kasa sayımının yanında bankanın da
 * doğrulanması için.
 *
 * İki rakam karşılaştırılır:
 *  - CANLI: bankanın gerçek bakiyesi (VakıfBank + Garanti, bankState/current)
 *  - SİSTEM: kasa işlemlerinden biriken havale/EFT toplamı (Banka modülündeki
 *    "Banka Bakiyesi" ile birebir aynı kural)
 *
 * Canlı Banka'daki her hareket kasaya Giriş/Çıkış olarak da düştüğü için bu
 * ikisi normalde birebir tutar (Kavaklı'da tutuyor). Tutmuyorsa bir hareket
 * yanlış hanede kaydedilmiş demektir.
 */
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { BANK_PROVIDER_LABELS, BANK_PROVIDERS, resolveBankingId } from './bankingSchema';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Banka bakiyesini artıran/azaltan işlem türleri (Banka modülüyle aynı). */
const ARTIRAN = ['Satış', 'Giriş'];
const AZALTAN = ['Alış', 'Çıkış', 'İade', 'Değişim'];

/** Canlı bakiye: VakıfBank kökte, Garanti bankProviders altında duruyor. */
export async function fetchLiveBankBalances(sube) {
  const bankingId = resolveBankingId(sube);
  const refs = [
    { provider: BANK_PROVIDERS.VAKIFBANK, ref: doc(db, 'subeler', bankingId, 'bankState', 'current') },
    { provider: BANK_PROVIDERS.GARANTI, ref: doc(db, 'subeler', bankingId, 'bankProviders', 'garanti', 'bankState', 'current') }
  ];
  const accounts = [];
  for (const item of refs) {
    // Bir banka okunamazsa diğerini de kaybetmeyelim; eksik olan işaretlenir.
    try {
      const snap = await getDoc(item.ref);
      if (!snap.exists()) {
        accounts.push({ provider: item.provider, label: BANK_PROVIDER_LABELS[item.provider], ok: false, reason: 'kayıt yok' });
        continue;
      }
      const data = snap.data() || {};
      const balance = data.currentBalance != null ? toNum(data.currentBalance)
        : data.balance != null ? toNum(data.balance)
        : data.availableBalance != null ? toNum(data.availableBalance)
        : null;
      accounts.push({
        provider: item.provider,
        label: BANK_PROVIDER_LABELS[item.provider],
        ok: balance != null,
        balance: balance != null ? round2(balance) : null,
        lastSyncAt: data.lastSyncAt?.toDate?.() || null,
        reason: balance == null ? 'bakiye alanı boş' : null
      });
    } catch (err) {
      accounts.push({
        provider: item.provider,
        label: BANK_PROVIDER_LABELS[item.provider],
        ok: false,
        reason: err?.message || 'okunamadı'
      });
    }
  }
  const okAccounts = accounts.filter((a) => a.ok);
  return {
    accounts,
    ok: okAccounts.length > 0 && okAccounts.length === accounts.length,
    total: okAccounts.length ? round2(okAccounts.reduce((s, a) => s + a.balance, 0)) : null
  };
}

/** Sistem bakiyesi: işlemlerdeki havale/EFT hareketlerinin kümülatif toplamı. */
export async function fetchSystemBankBalance(sube) {
  const snap = await getDocs(query(collection(db, 'subeler', sube, 'islemler'), where('havaleEft', '>', 0)));
  let total = 0;
  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const miktar = toNum(data.havaleEft);
    if (ARTIRAN.includes(data.islemTuru)) total += miktar;
    else if (AZALTAN.includes(data.islemTuru)) total -= miktar;
  });
  return round2(total);
}

/**
 * Gün sonu kontrolü. Dönen `blocking` yalnızca iki rakam da okunabildiği ve
 * fark çıktığı durumda true olur: banka servisine ulaşılamadığı için gün sonu
 * alınamaz duruma düşmesin.
 */
export async function checkBankBalance(sube) {
  if (!sube) return { ok: false, blocking: false, reason: 'Şube bilgisi yok.' };
  let live = null;
  let system = null;
  let reason = null;
  try {
    live = await fetchLiveBankBalances(sube);
  } catch (err) {
    reason = `Canlı banka bakiyesi okunamadı: ${err?.message || err}`;
  }
  try {
    system = await fetchSystemBankBalance(sube);
  } catch (err) {
    reason = reason || `Sistem banka bakiyesi hesaplanamadı: ${err?.message || err}`;
  }

  if (!live?.ok || live.total == null || system == null) {
    return {
      ok: false,
      blocking: false,
      live,
      liveTotal: live?.total ?? null,
      systemTotal: system,
      diff: null,
      reason: reason || 'Banka bakiyesi şu an okunamadı; kontrol atlandı.'
    };
  }

  const diff = round2(live.total - system);
  return {
    ok: Math.abs(diff) < 0.01,
    blocking: Math.abs(diff) >= 0.01,
    live,
    liveTotal: live.total,
    systemTotal: system,
    diff,
    reason: null
  };
}

export const formatTry = (n) =>
  `${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
